import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseApi, RequestType } from '@kapi';
import { EnvironmentVariablesService } from '@kenv';
import { EligibilityCandidate } from '@kp/eligibility/eligibility-candidate.model';
import { WorkbookRequest } from '@kp/workbook/models/workbook-request.model';
import { DataStoreService } from '@kservice';
import { HttpStatusCode } from '@ktypes/enums';
import { DataStatus, Status, StatusMessage } from '@ktypes/models';
import { firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class WorkbookApi extends BaseApi {
  constructor(
    private client: HttpClient,
    dataStoreService: DataStoreService,
    private _environmentVariablesService: EnvironmentVariablesService
  ) {
    super(client, dataStoreService, _environmentVariablesService);
  }

  async getWorkbookOrder(): Promise<DataStatus<WorkbookRequest>> {
    const url = this.buildUrl('/workbook', true);
    const request$ = this.performRequest(RequestType.GET, url, {
      includeToken: true,
    }).pipe(
      map((response: HttpResponse<WorkbookRequest>) => {
        if (response?.ok) {
          return new DataStatus<WorkbookRequest>(Status.done, new StatusMessage(Status.done, ''), response.body);
        }
        return new DataStatus<WorkbookRequest>(Status.error, new StatusMessage(Status.error, ''), null);
      }),
      catchError((err) => {
        console.warn('Failed getting workbook order: ', err);
        return of(new DataStatus<WorkbookRequest>(Status.error, new StatusMessage(Status.done, ''), null));
      })
    );
    return firstValueFrom(request$).catch((error) => {
      console.warn(`Error getting workbook order: `, error);
      return new DataStatus<WorkbookRequest>(Status.error, new StatusMessage(Status.error, ''), null);
    });
  }

  async createWorkBookRequest(candidate: EligibilityCandidate): Promise<DataStatus<string>> {
    const url = this.buildUrl('/workbook', true);
    let errorResponse = null;
    const request$ = this.performRequest<string>(RequestType.POST, url, {
      requestBody: candidate.serialize(),
      includeToken: true,
    }).pipe(
      catchError((err) => {
        console.warn(`Failed creating workbook order:`, err);
        return of(false);
      })
    );
    const response = await firstValueFrom(request$).catch((error) => {
      errorResponse = error;
      console.warn(`Error creating workbook response: `, error);
      return null;
    });
    if (response?.ok) {
      return new DataStatus(Status.done, new StatusMessage(Status.done, ''), response?.body || 'id');
    } else if (errorResponse?.status === HttpStatusCode.CONFLICT) {
      return new DataStatus(Status.done, new StatusMessage(Status.done, 'duplicate'), null);
    }
    return new DataStatus(Status.error, new StatusMessage(Status.error, null), null);
  }
}
